import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  updateFeatureClaim,
  fetchAllFeatureClaims,
} from '@store/actions/claimFeature.action'
import { AppDispatch } from '@store/index'
import Button from '@components/common/Button/Button.component'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import {
  Claim,
  InsurancePayment,
  isClaimApproved,
} from '@dataTypes/claimFeature.types'
import { Modal } from 'react-bootstrap'
import ClaimAttachmentTab from '@components/layout/Modals/AddClaims/ClaimAttachmentTab'
import ClaimInsurancePayment from '@components/layout/Modals/AddClaims/ClaimInsurancePayment'

import { logger } from '@utils/safeLogger'

type InsurancePaymentModalProps = {
  isVisible: boolean
  closeModal: () => void
  selectedClaim: Claim
}

const InsurancePaymentModal: React.FC<InsurancePaymentModalProps> = ({
  isVisible,
  closeModal,
  selectedClaim,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [activeTab, setActiveTab] = useState<'payment' | 'attachment'>(
    'payment',
  )

  const [localClaim, setLocalClaim] = useState<Claim>(selectedClaim)

  const isApproved = selectedClaim?.status
    ? isClaimApproved(selectedClaim.status)
    : false

  useEffect(() => {
    setLocalClaim(selectedClaim)
  }, [selectedClaim])

  const handleChangeTab = (tab: string): void => {
    if (tab === 'payment' || tab === 'attachment') {
      setActiveTab(tab as 'payment' | 'attachment')
    }
  }

  const handleUpdatePayments = (updatedPayments: InsurancePayment[]): void => {
    setLocalClaim((prevClaim) => ({
      ...prevClaim,
      insurancePayments: updatedPayments,
    }))
  }

  const handleUpdateAttachments = (updatedClaim: Claim): void => {
    setLocalClaim(updatedClaim)
  }

  const handleSave = async (): Promise<void> => {
    try {
      await dispatch(
        updateFeatureClaim({ id: localClaim.id, body: localClaim }),
      ).unwrap()
      await dispatch(fetchAllFeatureClaims())
      closeModal()
    } catch (error) {
      logger.error('Error updating claim:', error)
    }
  }

  const tabButtons = [
    { label: 'Payment', value: 'payment' },
    { label: 'Attachment', value: 'attachment' },
  ]

  return (
    <Modal
      show={isVisible}
      onHide={closeModal}
      backdrop="static"
      keyboard={false}
      size="lg"
      className={isApproved ? 'disabled-claims-modal' : ''}
    >
      <Modal.Header className="bg-custom3" closeButton>
        <Modal.Title className="fs-5 text-dark">Insurance payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ButtonGroup
          buttons={tabButtons}
          activeTab={activeTab}
          onButtonClick={handleChangeTab}
          className="insurance-payment-tabs-group"
        />

        {activeTab === 'payment' && (
          <ClaimInsurancePayment
            selectedClaim={localClaim}
            insurancePayments={localClaim.insurancePayments || []}
            onUpdateInsurancePayments={handleUpdatePayments}
          />
        )}

        {activeTab === 'attachment' && (
          <ClaimAttachmentTab
            selectedClaim={localClaim}
            onChangeClaim={handleUpdateAttachments}
            isEditingExistingClaim={true}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          size="md"
          className="button-secondary me-1"
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button size="md" className="button-primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default InsurancePaymentModal
